import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, ChevronDown, CheckCircle2, Circle, Plus, AlertTriangle,
  ClipboardCheck, Brain, MessageSquare, Calendar, Stethoscope,
  ShieldAlert, Video, Save, Loader2, X, Clock, Users, FileText
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import Toggle from "../ui/Toggle";

// ── Types ─────────────────────────────────────────────────────────────────────
interface RiskFactor {
  key: string;
  label: string;
  checked: boolean;
  autoDetected?: boolean;
}

interface BanksGoodfellow {
  criterionKey: string;
  criterionLabel: string;
  rating: "clearly_demonstrated" | "partially_demonstrated" | "concerns_noted" | null;
  notes: string;
  evidenceDocumentIds: string[];
}

interface InterviewQuestion {
  questionText: string;
  response: string;
  isCustom: boolean;
  answeredAt: string | null;
}

interface MeetingAttendee {
  name: string;
  role: string;
}

interface Meeting {
  date: string;
  title: string;
  duration: number;
  attendees: MeetingAttendee[];
  clientDemeanor: string;
  consistencyNotes: string;
  beneficiaryPresent: boolean;
  beneficiaryNotes: string;
}

interface MedicalEvaluation {
  evaluationType: "capacity_letter" | "targeted_evaluation" | "comprehensive_forensic";
  evaluatorName: string;
  evaluatorCredentials: string;
  date: string;
  screeningTool: string;
  score: string;
  outcome: "capacity_confirmed" | "capacity_questionable" | "lacks_capacity" | "inconclusive";
  reportDocumentId: string;
  notes: string;
}

interface UndueInfluenceQuestion {
  questionText: string;
  answer: boolean | null;
  notes: string;
}

interface VideoRecording {
  date: string;
  duration: number;
  consentFormDocumentId: string;
  consentStatus: "signed" | "pending" | "declined";
  videoDocumentId: string;
  integrityNotes: string;
  chainOfCustody: string;
}

interface CapacityAssessment {
  riskLevel: "standard" | "elevated" | "high";
  riskLevelOverride: boolean;
  riskFactors: RiskFactor[];
  riskNotes: string;
  banksGoodfellow: BanksGoodfellow[];
  interviewQuestions: InterviewQuestion[];
  meetings: Meeting[];
  medicalEvaluations: MedicalEvaluation[];
  undueInfluenceScreening: UndueInfluenceQuestion[];
  videoRecordings: VideoRecording[];
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

interface Props {
  matterId: string;
  caseData: any;
  onUpdate: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const DEFAULT_RISK_FACTORS: RiskFactor[] = [
  { key: "age_over_70", label: "Testator is 70+ years old", checked: false },
  { key: "cognitive_decline", label: "History of cognitive decline or dementia", checked: false },
  { key: "recent_diagnosis", label: "Recent diagnosis affecting cognition", checked: false },
  { key: "family_dispute", label: "Known family disputes about inheritance", checked: false },
  { key: "significant_change", label: "Significant changes from prior wills", checked: false },
  { key: "isolated_testator", label: "Testator may be isolated or dependent", checked: false },
  { key: "substance_abuse", label: "History of substance abuse", checked: false },
  { key: "beneficiary_involvement", label: "Beneficiary involved in will preparation", checked: false },
];

const DEFAULT_BANKS_GOODFELLOW: BanksGoodfellow[] = [
  { criterionKey: "nature_of_act", criterionLabel: "Understanding the nature and effect of making a will", rating: null, notes: "", evidenceDocumentIds: [] },
  { criterionKey: "extent_of_property", criterionLabel: "Understanding the extent of property being disposed", rating: null, notes: "", evidenceDocumentIds: [] },
  { criterionKey: "claims_of_persons", criterionLabel: "Understanding the claims of persons who might be expected to benefit", rating: null, notes: "", evidenceDocumentIds: [] },
  { criterionKey: "no_disorder_of_mind", criterionLabel: "No disorder of the mind that influences the testator's dispositions", rating: null, notes: "", evidenceDocumentIds: [] },
];

const DEFAULT_INTERVIEW_QUESTIONS: InterviewQuestion[] = [
  { questionText: "Can you describe your family members and your relationship with each of them?", response: "", isCustom: false, answeredAt: null },
  { questionText: "Can you describe your assets and their approximate values?", response: "", isCustom: false, answeredAt: null },
  { questionText: "Can you explain who you want to receive your assets and why?", response: "", isCustom: false, answeredAt: null },
  { questionText: "Has anyone pressured you or suggested how your will should be written?", response: "", isCustom: false, answeredAt: null },
  { questionText: "Do you understand that this document will determine what happens to your property after death?", response: "", isCustom: false, answeredAt: null },
  { questionText: "Have you changed your mind about any previous will, and if so, why?", response: "", isCustom: false, answeredAt: null },
];

const DEFAULT_UNDUE_INFLUENCE: UndueInfluenceQuestion[] = [
  { questionText: "Was the testator isolated from family or friends?", answer: null, notes: "" },
  { questionText: "Did any beneficiary participate in selecting the attorney?", answer: null, notes: "" },
  { questionText: "Was a beneficiary present during instruction-giving?", answer: null, notes: "" },
  { questionText: "Has the testator recently become dependent on any beneficiary?", answer: null, notes: "" },
  { questionText: "Were there sudden changes in the estate plan favoring a new party?", answer: null, notes: "" },
  { questionText: "Does the testator appear to be under duress or coercion?", answer: null, notes: "" },
];

const EMPTY_MEETING: Meeting = {
  date: "", title: "", duration: 30, attendees: [{ name: "", role: "" }],
  clientDemeanor: "", consistencyNotes: "", beneficiaryPresent: false, beneficiaryNotes: "",
};

const EMPTY_EVALUATION: MedicalEvaluation = {
  evaluationType: "capacity_letter", evaluatorName: "", evaluatorCredentials: "",
  date: "", screeningTool: "", score: "", outcome: "capacity_confirmed",
  reportDocumentId: "", notes: "",
};

const EMPTY_RECORDING: VideoRecording = {
  date: "", duration: 0, consentFormDocumentId: "", consentStatus: "pending",
  videoDocumentId: "", integrityNotes: "", chainOfCustody: "",
};

const RISK_LEVEL_CONFIG = {
  standard: { label: "Standard", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", description: "No elevated risk factors identified. Standard documentation applies." },
  elevated: { label: "Elevated", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500", description: "Some risk factors present. Enhanced documentation recommended." },
  high: { label: "High", color: "bg-red-100 text-red-700", dot: "bg-red-500", description: "Multiple risk factors present. Comprehensive capacity evidence essential." },
};

const RATING_CONFIG = {
  clearly_demonstrated: { label: "Clearly Demonstrated", color: "bg-emerald-100 text-emerald-700 border-emerald-200", activeColor: "bg-emerald-500 text-white border-emerald-500" },
  partially_demonstrated: { label: "Partially Demonstrated", color: "bg-amber-100 text-amber-700 border-amber-200", activeColor: "bg-amber-500 text-white border-amber-500" },
  concerns_noted: { label: "Concerns Noted", color: "bg-red-100 text-red-700 border-red-200", activeColor: "bg-red-500 text-white border-red-500" },
};

const EVAL_TYPE_CONFIG = {
  capacity_letter: { label: "Capacity Letter", color: "bg-blue-100 text-blue-700" },
  targeted_evaluation: { label: "Targeted Evaluation", color: "bg-purple-100 text-purple-700" },
  comprehensive_forensic: { label: "Comprehensive Forensic", color: "bg-amber-100 text-amber-700" },
};

const OUTCOME_CONFIG = {
  capacity_confirmed: { label: "Capacity Confirmed", color: "bg-emerald-100 text-emerald-700" },
  capacity_questionable: { label: "Questionable", color: "bg-amber-100 text-amber-700" },
  lacks_capacity: { label: "Lacks Capacity", color: "bg-red-100 text-red-700" },
  inconclusive: { label: "Inconclusive", color: "bg-slate-100 text-slate-700" },
};

const CONSENT_CONFIG = {
  signed: { label: "Signed", color: "bg-emerald-100 text-emerald-700" },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700" },
  declined: { label: "Declined", color: "bg-red-100 text-red-700" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatLabel = (s: string) => s?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";
const formatDate = (d: string) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
const formatDuration = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 > 0 ? `${m % 60}m` : ""}`.trim() : `${m}m`;

// ── Collapsible Section Wrapper ───────────────────────────────────────────────
const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  badge?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  warning?: string;
}> = ({ title, icon, iconBg, badge, isOpen, onToggle, children, warning }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
    <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-xl ${iconBg} flex items-center justify-center`}>{icon}</div>
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {badge}
      </div>
      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
          <div className="px-4 pb-4 space-y-4">
            {warning && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {warning}
              </div>
            )}
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// ── Component ─────────────────────────────────────────────────────────────────
const CapacityAssessmentTab: React.FC<Props> = ({ matterId, caseData, onUpdate }) => {
  const existing: Partial<CapacityAssessment> = caseData?.capacityAssessment || {};

  // ── State ───────────────────────────────────────────────────────────────
  const [riskFactors, setRiskFactors] = useState<RiskFactor[]>(
    existing.riskFactors?.length ? existing.riskFactors : [...DEFAULT_RISK_FACTORS]
  );
  const [riskLevelOverride, setRiskLevelOverride] = useState(existing.riskLevelOverride ?? false);
  const [manualRiskLevel, setManualRiskLevel] = useState<"standard" | "elevated" | "high">(existing.riskLevel ?? "standard");
  const [riskNotes, setRiskNotes] = useState(existing.riskNotes ?? "");

  const [banksGoodfellow, setBanksGoodfellow] = useState<BanksGoodfellow[]>(
    existing.banksGoodfellow?.length ? existing.banksGoodfellow : [...DEFAULT_BANKS_GOODFELLOW]
  );

  const [interviewQuestions, setInterviewQuestions] = useState<InterviewQuestion[]>(
    existing.interviewQuestions?.length ? existing.interviewQuestions : [...DEFAULT_INTERVIEW_QUESTIONS]
  );
  const [newQuestionText, setNewQuestionText] = useState("");

  const [meetings, setMeetings] = useState<Meeting[]>(existing.meetings || []);
  const [medicalEvaluations, setMedicalEvaluations] = useState<MedicalEvaluation[]>(existing.medicalEvaluations || []);
  const [undueInfluence, setUndueInfluence] = useState<UndueInfluenceQuestion[]>(
    existing.undueInfluenceScreening?.length ? existing.undueInfluenceScreening : [...DEFAULT_UNDUE_INFLUENCE]
  );
  const [videoRecordings, setVideoRecordings] = useState<VideoRecording[]>(existing.videoRecordings || []);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    risk: true, banks: true, interview: false, meetings: false, medical: false, undue: false, video: false,
  });

  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState<Meeting>({ ...EMPTY_MEETING });
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [evalForm, setEvalForm] = useState<MedicalEvaluation>({ ...EMPTY_EVALUATION });
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingForm, setRecordingForm] = useState<VideoRecording>({ ...EMPTY_RECORDING });

  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // ── Computed ─────────────────────────────────────────────────────────────
  const checkedCount = useMemo(() => riskFactors.filter(f => f.checked).length, [riskFactors]);

  const calculatedRiskLevel = useMemo((): "standard" | "elevated" | "high" => {
    if (riskLevelOverride) return manualRiskLevel;
    if (checkedCount >= 4) return "high";
    if (checkedCount >= 2) return "elevated";
    return "standard";
  }, [checkedCount, riskLevelOverride, manualRiskLevel]);

  const banksRatedCount = useMemo(() => banksGoodfellow.filter(b => b.rating !== null).length, [banksGoodfellow]);
  const answeredCount = useMemo(() => interviewQuestions.filter(q => q.response.trim().length > 0).length, [interviewQuestions]);
  const undueYesCount = useMemo(() => undueInfluence.filter(q => q.answer === true).length, [undueInfluence]);

  const completedSections = useMemo(() => {
    let count = 0;
    if (checkedCount > 0 || riskNotes.trim()) count++;
    if (banksRatedCount > 0) count++;
    if (answeredCount > 0) count++;
    if (meetings.length > 0) count++;
    if (medicalEvaluations.length > 0) count++;
    if (undueInfluence.some(q => q.answer !== null)) count++;
    if (videoRecordings.length > 0) count++;
    return count;
  }, [checkedCount, riskNotes, banksRatedCount, answeredCount, meetings, medicalEvaluations, undueInfluence, videoRecordings]);

  // ── Dirty tracking ──────────────────────────────────────────────────────
  const markDirty = useCallback(() => { if (!isDirty) setIsDirty(true); }, [isDirty]);

  // ── Section toggle ──────────────────────────────────────────────────────
  const toggleSection = (key: string) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const updatedData: CapacityAssessment = {
        riskLevel: calculatedRiskLevel,
        riskLevelOverride,
        riskFactors,
        riskNotes,
        banksGoodfellow,
        interviewQuestions,
        meetings,
        medicalEvaluations,
        undueInfluenceScreening: undueInfluence,
        videoRecordings,
      };
      await estatesApi.update(caseData._id, { capacityAssessment: updatedData });
      onUpdate();
      setIsDirty(false);
      setSavedMessage(true);
      setTimeout(() => setSavedMessage(false), 2500);
    } catch (err) {
      console.error("Failed to save capacity assessment:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Risk factor toggle ──────────────────────────────────────────────────
  const toggleRiskFactor = (key: string) => {
    setRiskFactors(prev => prev.map(f => f.key === key ? { ...f, checked: !f.checked } : f));
    markDirty();
  };

  // ── Banks v. Goodfellow update ──────────────────────────────────────────
  const updateBanks = (idx: number, updates: Partial<BanksGoodfellow>) => {
    setBanksGoodfellow(prev => prev.map((b, i) => i === idx ? { ...b, ...updates } : b));
    markDirty();
  };

  // ── Interview questions ─────────────────────────────────────────────────
  const updateQuestion = (idx: number, response: string) => {
    setInterviewQuestions(prev => prev.map((q, i) =>
      i === idx ? { ...q, response, answeredAt: response.trim() ? new Date().toISOString() : null } : q
    ));
    markDirty();
  };

  const addCustomQuestion = () => {
    if (!newQuestionText.trim()) return;
    setInterviewQuestions(prev => [...prev, { questionText: newQuestionText.trim(), response: "", isCustom: true, answeredAt: null }]);
    setNewQuestionText("");
    markDirty();
  };

  // ── Meetings ────────────────────────────────────────────────────────────
  const addMeeting = () => {
    setMeetings(prev => [...prev, { ...meetingForm }]);
    setShowMeetingModal(false);
    setMeetingForm({ ...EMPTY_MEETING });
    markDirty();
  };

  const removeMeeting = (idx: number) => {
    setMeetings(prev => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  // ── Medical evaluations ─────────────────────────────────────────────────
  const addEvaluation = () => {
    setMedicalEvaluations(prev => [...prev, { ...evalForm }]);
    setShowEvalModal(false);
    setEvalForm({ ...EMPTY_EVALUATION });
    markDirty();
  };

  const removeEvaluation = (idx: number) => {
    setMedicalEvaluations(prev => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  // ── Undue influence ─────────────────────────────────────────────────────
  const updateUndueInfluence = (idx: number, updates: Partial<UndueInfluenceQuestion>) => {
    setUndueInfluence(prev => prev.map((q, i) => i === idx ? { ...q, ...updates } : q));
    markDirty();
  };

  // ── Video recordings ────────────────────────────────────────────────────
  const addRecording = () => {
    setVideoRecordings(prev => [...prev, { ...recordingForm }]);
    setShowRecordingModal(false);
    setRecordingForm({ ...EMPTY_RECORDING });
    markDirty();
  };

  const removeRecording = (idx: number) => {
    setVideoRecordings(prev => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  // ── Meeting modal attendee helpers ──────────────────────────────────────
  const addAttendee = () => setMeetingForm(prev => ({ ...prev, attendees: [...prev.attendees, { name: "", role: "" }] }));
  const removeAttendee = (idx: number) => setMeetingForm(prev => ({ ...prev, attendees: prev.attendees.filter((_, i) => i !== idx) }));
  const updateAttendee = (idx: number, field: "name" | "role", value: string) => {
    setMeetingForm(prev => ({ ...prev, attendees: prev.attendees.map((a, i) => i === idx ? { ...a, [field]: value } : a) }));
  };

  const riskCfg = RISK_LEVEL_CONFIG[calculatedRiskLevel];

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Shield className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Testamentary Capacity Assessment</h2>
              <p className="text-sm text-slate-500">{completedSections} of 7 sections with data</p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${riskCfg.color}`}>
            <span className={`h-2 w-2 rounded-full ${riskCfg.dot}`} />
            {riskCfg.label} Risk
          </span>
        </div>
      </div>

      {/* 1. Risk Assessment */}
      <CollapsibleSection
        title="Risk Assessment"
        icon={<AlertTriangle className="h-4.5 w-4.5 text-amber-600" />}
        iconBg="bg-amber-50"
        badge={<span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${riskCfg.color}`}>{riskCfg.label}</span>}
        isOpen={expandedSections.risk}
        onToggle={() => toggleSection("risk")}
      >
        {/* Risk level indicator */}
        <div className={`flex items-start gap-3 p-3 rounded-xl border ${
          calculatedRiskLevel === "high" ? "bg-red-50/50 border-red-200" :
          calculatedRiskLevel === "elevated" ? "bg-amber-50/50 border-amber-200" :
          "bg-emerald-50/50 border-emerald-200"
        }`}>
          <span className={`mt-0.5 h-3 w-3 rounded-full flex-shrink-0 ${riskCfg.dot}`} />
          <div>
            <p className="text-sm font-medium text-slate-700">{riskCfg.label} Risk Level</p>
            <p className="text-xs text-slate-500 mt-0.5">{riskCfg.description}</p>
          </div>
        </div>

        {/* Risk factors checklist */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">Risk Factors ({checkedCount} identified)</p>
          {riskFactors.map(f => (
            <label key={f.key} className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors cursor-pointer group">
              <input
                type="checkbox"
                checked={f.checked}
                onChange={() => toggleRiskFactor(f.key)}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className={`text-sm flex-1 ${f.checked ? "text-slate-800 font-medium" : "text-slate-600"}`}>{f.label}</span>
              {f.autoDetected && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-600">Auto</span>
              )}
            </label>
          ))}
        </div>

        {/* Manual override toggle */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
          <div>
            <p className="text-sm font-medium text-slate-700">Manual Risk Level Override</p>
            <p className="text-xs text-slate-500">Override auto-calculated risk level</p>
          </div>
          <Toggle
            checked={riskLevelOverride}
            onChange={(val) => { setRiskLevelOverride(val); markDirty(); }}
          />
        </div>

        {riskLevelOverride && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="flex gap-2">
            {(["standard", "elevated", "high"] as const).map(level => (
              <button
                key={level}
                onClick={() => { setManualRiskLevel(level); markDirty(); }}
                className={`flex-1 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                  manualRiskLevel === level
                    ? level === "high" ? "border-red-500 bg-red-50 text-red-700"
                      : level === "elevated" ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}
              >
                {formatLabel(level)}
              </button>
            ))}
          </motion.div>
        )}

        {/* Risk notes */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Risk Assessment Notes</label>
          <textarea
            value={riskNotes}
            onChange={e => { setRiskNotes(e.target.value); markDirty(); }}
            rows={3}
            placeholder="Document any additional risk observations or context..."
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </CollapsibleSection>

      {/* 2. Banks v. Goodfellow */}
      <CollapsibleSection
        title="Banks v. Goodfellow Assessment"
        icon={<Brain className="h-4.5 w-4.5 text-purple-600" />}
        iconBg="bg-purple-50"
        badge={<span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">{banksRatedCount}/4 rated</span>}
        isOpen={expandedSections.banks}
        onToggle={() => toggleSection("banks")}
      >
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-medium text-slate-500">Completion</p>
            <p className="text-xs font-medium text-slate-700">{banksRatedCount} of 4 criteria rated</p>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#0F5132] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(banksRatedCount / 4) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {banksGoodfellow.map((criterion, idx) => (
          <div key={criterion.criterionKey} className="p-4 rounded-xl border border-slate-200 space-y-3">
            <p className="text-sm font-medium text-slate-800">{idx + 1}. {criterion.criterionLabel}</p>
            <div className="flex gap-2">
              {(Object.keys(RATING_CONFIG) as Array<keyof typeof RATING_CONFIG>).map(rating => {
                const cfg = RATING_CONFIG[rating];
                const isActive = criterion.rating === rating;
                return (
                  <button
                    key={rating}
                    onClick={() => updateBanks(idx, { rating })}
                    className={`flex-1 py-2 px-2 rounded-xl text-xs font-medium border transition-all ${isActive ? cfg.activeColor : `${cfg.color} border hover:opacity-80`}`}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            <textarea
              value={criterion.notes}
              onChange={e => updateBanks(idx, { notes: e.target.value })}
              rows={2}
              placeholder="Supporting observations and evidence..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        ))}
      </CollapsibleSection>

      {/* 3. Interview Questions */}
      <CollapsibleSection
        title="Interview Questions"
        icon={<MessageSquare className="h-4.5 w-4.5 text-blue-600" />}
        iconBg="bg-blue-50"
        badge={<span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{answeredCount}/{interviewQuestions.length} answered</span>}
        isOpen={expandedSections.interview}
        onToggle={() => toggleSection("interview")}
      >
        {interviewQuestions.map((q, idx) => (
          <div key={idx} className="p-4 rounded-xl border border-slate-200 space-y-2">
            <div className="flex items-start gap-2">
              {q.response.trim() ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              ) : (
                <Circle className="h-5 w-5 text-slate-300 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-800">{q.questionText}</p>
                  {q.isCustom && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-600">Custom</span>}
                </div>
                {q.answeredAt && (
                  <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Answered {formatDate(q.answeredAt)}
                  </p>
                )}
              </div>
            </div>
            <textarea
              value={q.response}
              onChange={e => updateQuestion(idx, e.target.value)}
              rows={2}
              placeholder="Record the testator's response..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        ))}

        {/* Add custom question */}
        <div className="flex gap-2">
          <input
            value={newQuestionText}
            onChange={e => setNewQuestionText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addCustomQuestion()}
            placeholder="Add a custom question..."
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <button onClick={addCustomQuestion} disabled={!newQuestionText.trim()} className="px-4 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </CollapsibleSection>

      {/* 4. Meeting Log */}
      <CollapsibleSection
        title="Meeting Log"
        icon={<Calendar className="h-4.5 w-4.5 text-cyan-600" />}
        iconBg="bg-cyan-50"
        badge={<span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">{meetings.length} meeting{meetings.length !== 1 ? "s" : ""}</span>}
        isOpen={expandedSections.meetings}
        onToggle={() => toggleSection("meetings")}
      >
        {meetings.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">No meetings recorded yet</p>
        )}

        {meetings.map((m, idx) => (
          <motion.div key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl border border-slate-200 space-y-2">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-800">{m.title || "Untitled Meeting"}</p>
                  {m.beneficiaryPresent && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">
                      <AlertTriangle className="h-3 w-3" /> Beneficiary Present
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  {m.date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(m.date)}</span>}
                  {m.duration > 0 && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDuration(m.duration)}</span>}
                  {m.attendees.length > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {m.attendees.filter(a => a.name).length} attendee{m.attendees.filter(a => a.name).length !== 1 ? "s" : ""}</span>}
                </div>
              </div>
              <button onClick={() => removeMeeting(idx)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {m.clientDemeanor && (
              <p className="text-xs text-slate-600"><span className="font-medium">Demeanor:</span> {m.clientDemeanor}</p>
            )}
            {m.consistencyNotes && (
              <p className="text-xs text-slate-600"><span className="font-medium">Consistency:</span> {m.consistencyNotes}</p>
            )}
            {m.beneficiaryPresent && m.beneficiaryNotes && (
              <p className="text-xs text-amber-700"><span className="font-medium">Beneficiary Notes:</span> {m.beneficiaryNotes}</p>
            )}
          </motion.div>
        ))}

        <button onClick={() => setShowMeetingModal(true)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0F5132] py-2 px-3 transition-colors">
          <Plus className="h-3.5 w-3.5" /> Add Meeting
        </button>
      </CollapsibleSection>

      {/* 5. Medical Evaluations */}
      <CollapsibleSection
        title="Medical Evaluations"
        icon={<Stethoscope className="h-4.5 w-4.5 text-rose-600" />}
        iconBg="bg-rose-50"
        badge={<span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">{medicalEvaluations.length} evaluation{medicalEvaluations.length !== 1 ? "s" : ""}</span>}
        isOpen={expandedSections.medical}
        onToggle={() => toggleSection("medical")}
      >
        {medicalEvaluations.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">No medical evaluations recorded yet</p>
        )}

        {medicalEvaluations.map((ev, idx) => {
          const typeCfg = EVAL_TYPE_CONFIG[ev.evaluationType];
          const outcomeCfg = OUTCOME_CONFIG[ev.outcome];
          return (
            <motion.div key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl border border-slate-200 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${typeCfg.color}`}>{typeCfg.label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${outcomeCfg.color}`}>{outcomeCfg.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{ev.evaluatorName || "Unknown Evaluator"}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                    {ev.evaluatorCredentials && <span>{ev.evaluatorCredentials}</span>}
                    {ev.date && <span>{formatDate(ev.date)}</span>}
                    {ev.screeningTool && <span>Tool: {ev.screeningTool}</span>}
                    {ev.score && <span>Score: {ev.score}</span>}
                  </div>
                </div>
                <button onClick={() => removeEvaluation(idx)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {ev.notes && <p className="text-xs text-slate-600">{ev.notes}</p>}
            </motion.div>
          );
        })}

        <button onClick={() => setShowEvalModal(true)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0F5132] py-2 px-3 transition-colors">
          <Plus className="h-3.5 w-3.5" /> Add Evaluation
        </button>
      </CollapsibleSection>

      {/* 6. Undue Influence Screening */}
      <CollapsibleSection
        title="Undue Influence Screening"
        icon={<ShieldAlert className="h-4.5 w-4.5 text-red-600" />}
        iconBg="bg-red-50"
        badge={undueYesCount > 0 ? (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{undueYesCount} concern{undueYesCount !== 1 ? "s" : ""}</span>
        ) : undefined}
        isOpen={expandedSections.undue}
        onToggle={() => toggleSection("undue")}
        warning={undueYesCount > 0 ? `${undueYesCount} potential concern${undueYesCount !== 1 ? "s" : ""} identified. Review carefully and document mitigation steps.` : undefined}
      >
        {undueInfluence.map((q, idx) => (
          <div key={idx} className={`p-4 rounded-xl border space-y-2 transition-colors ${q.answer === true ? "border-red-200 bg-red-50/30" : "border-slate-200"}`}>
            <p className="text-sm font-medium text-slate-800">{q.questionText}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => updateUndueInfluence(idx, { answer: q.answer === true ? null : true })}
                className={`px-4 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                  q.answer === true ? "bg-red-500 text-white border-red-500" : "border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                }`}
              >
                Yes
              </button>
              <button
                onClick={() => updateUndueInfluence(idx, { answer: q.answer === false ? null : false })}
                className={`px-4 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                  q.answer === false ? "bg-emerald-500 text-white border-emerald-500" : "border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-600"
                }`}
              >
                No
              </button>
            </div>
            <input
              value={q.notes}
              onChange={e => updateUndueInfluence(idx, { notes: e.target.value })}
              placeholder="Additional notes..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        ))}
      </CollapsibleSection>

      {/* 7. Video Recordings */}
      <CollapsibleSection
        title="Video Recordings"
        icon={<Video className="h-4.5 w-4.5 text-indigo-600" />}
        iconBg="bg-indigo-50"
        badge={<span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">{videoRecordings.length} recording{videoRecordings.length !== 1 ? "s" : ""}</span>}
        isOpen={expandedSections.video}
        onToggle={() => toggleSection("video")}
      >
        {videoRecordings.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">No video recordings logged yet</p>
        )}

        {videoRecordings.map((vr, idx) => {
          const consentCfg = CONSENT_CONFIG[vr.consentStatus];
          return (
            <motion.div key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl border border-slate-200 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-indigo-500" />
                    <p className="text-sm font-semibold text-slate-800">Recording {formatDate(vr.date)}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${consentCfg.color}`}>Consent: {consentCfg.label}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    {vr.duration > 0 && <span>{formatDuration(vr.duration)}</span>}
                  </div>
                </div>
                <button onClick={() => removeRecording(idx)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {vr.integrityNotes && <p className="text-xs text-slate-600"><span className="font-medium">Integrity:</span> {vr.integrityNotes}</p>}
              {vr.chainOfCustody && <p className="text-xs text-slate-600"><span className="font-medium">Chain of Custody:</span> {vr.chainOfCustody}</p>}
            </motion.div>
          );
        })}

        <button onClick={() => setShowRecordingModal(true)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0F5132] py-2 px-3 transition-colors">
          <Plus className="h-3.5 w-3.5" /> Add Recording
        </button>
      </CollapsibleSection>

      {/* ── Floating Save Button ────────────────────────────────────────────── */}
      <AnimatePresence>
        {isDirty && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-30 flex items-center gap-3"
          >
            {savedMessage && (
              <motion.span initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="px-3 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium shadow-lg">
                Saved
              </motion.span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-[#0F5132] text-white rounded-xl text-sm font-bold hover:bg-[#146C43] disabled:opacity-50 shadow-lg transition-colors"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Also show saved message when not dirty (after save completes) */}
      <AnimatePresence>
        {savedMessage && !isDirty && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-30"
          >
            <span className="px-4 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium shadow-lg flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Add Meeting Modal ──────────────────────────────────────────────── */}
      <PremiumModal
        open={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        title="Add Meeting"
        subtitle="Record a client meeting for capacity documentation"
        icon={<Calendar className="h-5 w-5" />}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
              <input type="date" value={meetingForm.date} onChange={e => setMeetingForm({ ...meetingForm, date: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
              <input value={meetingForm.title} onChange={e => setMeetingForm({ ...meetingForm, title: e.target.value })} placeholder="e.g., Initial Will Instructions" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Duration (min)</label>
              <input type="number" min={0} value={meetingForm.duration} onChange={e => setMeetingForm({ ...meetingForm, duration: parseInt(e.target.value) || 0 })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
          </div>

          {/* Attendees */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Attendees</label>
            {meetingForm.attendees.map((att, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input value={att.name} onChange={e => updateAttendee(idx, "name", e.target.value)} placeholder="Name" className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                <input value={att.role} onChange={e => updateAttendee(idx, "role", e.target.value)} placeholder="Role" className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                {meetingForm.attendees.length > 1 && (
                  <button onClick={() => removeAttendee(idx)} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={addAttendee} className="flex items-center gap-1 text-xs text-slate-400 hover:text-[#0F5132] transition-colors mt-1">
              <Plus className="h-3 w-3" /> Add Attendee
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client Demeanor</label>
            <textarea value={meetingForm.clientDemeanor} onChange={e => setMeetingForm({ ...meetingForm, clientDemeanor: e.target.value })} rows={2} placeholder="Describe the client's demeanor, alertness, and engagement..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Consistency Notes</label>
            <textarea value={meetingForm.consistencyNotes} onChange={e => setMeetingForm({ ...meetingForm, consistencyNotes: e.target.value })} rows={2} placeholder="Was the client's account consistent with prior meetings?" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          </div>

          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
            <input type="checkbox" checked={meetingForm.beneficiaryPresent} onChange={e => setMeetingForm({ ...meetingForm, beneficiaryPresent: e.target.checked })} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            <div>
              <p className="text-sm font-medium text-slate-700">Beneficiary was present during meeting</p>
              <p className="text-xs text-slate-500">This will be flagged in the capacity assessment record</p>
            </div>
          </div>

          {meetingForm.beneficiaryPresent && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
              <label className="block text-sm font-medium text-slate-700 mb-1">Beneficiary Presence Notes</label>
              <textarea value={meetingForm.beneficiaryNotes} onChange={e => setMeetingForm({ ...meetingForm, beneficiaryNotes: e.target.value })} rows={2} placeholder="Explain who was present and the circumstances..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </motion.div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => setShowMeetingModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
            <button onClick={addMeeting} disabled={!meetingForm.date || !meetingForm.title} className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Add Meeting
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Add Medical Evaluation Modal ───────────────────────────────────── */}
      <PremiumModal
        open={showEvalModal}
        onClose={() => setShowEvalModal(false)}
        title="Add Medical Evaluation"
        subtitle="Record a medical or psychological capacity evaluation"
        icon={<Stethoscope className="h-5 w-5" />}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Evaluation Type *</label>
            <div className="flex gap-2">
              {(Object.keys(EVAL_TYPE_CONFIG) as Array<keyof typeof EVAL_TYPE_CONFIG>).map(type => {
                const cfg = EVAL_TYPE_CONFIG[type];
                const isActive = evalForm.evaluationType === type;
                return (
                  <button key={type} onClick={() => setEvalForm({ ...evalForm, evaluationType: type })} className={`flex-1 py-2.5 rounded-xl text-xs font-medium border-2 transition-all ${isActive ? `${cfg.color} border-current` : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Evaluator Name *</label>
              <input value={evalForm.evaluatorName} onChange={e => setEvalForm({ ...evalForm, evaluatorName: e.target.value })} placeholder="Dr. Jane Smith" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Credentials</label>
              <input value={evalForm.evaluatorCredentials} onChange={e => setEvalForm({ ...evalForm, evaluatorCredentials: e.target.value })} placeholder="MD, FRCPC — Geriatric Psychiatry" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
              <input type="date" value={evalForm.date} onChange={e => setEvalForm({ ...evalForm, date: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Screening Tool</label>
              <input value={evalForm.screeningTool} onChange={e => setEvalForm({ ...evalForm, screeningTool: e.target.value })} placeholder="e.g., MMSE, MoCA" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Score</label>
              <input value={evalForm.score} onChange={e => setEvalForm({ ...evalForm, score: e.target.value })} placeholder="e.g., 28/30" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Outcome *</label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(OUTCOME_CONFIG) as Array<keyof typeof OUTCOME_CONFIG>).map(outcome => {
                const cfg = OUTCOME_CONFIG[outcome];
                const isActive = evalForm.outcome === outcome;
                return (
                  <button key={outcome} onClick={() => setEvalForm({ ...evalForm, outcome })} className={`py-2 rounded-xl text-xs font-medium border-2 transition-all ${isActive ? `${cfg.color} border-current` : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={evalForm.notes} onChange={e => setEvalForm({ ...evalForm, notes: e.target.value })} rows={3} placeholder="Key findings, recommendations, and clinical impressions..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => setShowEvalModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
            <button onClick={addEvaluation} disabled={!evalForm.evaluatorName || !evalForm.date} className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Add Evaluation
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Add Video Recording Modal ──────────────────────────────────────── */}
      <PremiumModal
        open={showRecordingModal}
        onClose={() => setShowRecordingModal(false)}
        title="Add Video Recording"
        subtitle="Log a video recording of will execution or interview"
        icon={<Video className="h-5 w-5" />}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
              <input type="date" value={recordingForm.date} onChange={e => setRecordingForm({ ...recordingForm, date: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Duration (minutes)</label>
              <input type="number" min={0} value={recordingForm.duration} onChange={e => setRecordingForm({ ...recordingForm, duration: parseInt(e.target.value) || 0 })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Consent Status *</label>
            <div className="flex gap-2">
              {(Object.keys(CONSENT_CONFIG) as Array<keyof typeof CONSENT_CONFIG>).map(status => {
                const cfg = CONSENT_CONFIG[status];
                const isActive = recordingForm.consentStatus === status;
                return (
                  <button key={status} onClick={() => setRecordingForm({ ...recordingForm, consentStatus: status })} className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all ${isActive ? `${cfg.color} border-current` : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Integrity Notes</label>
            <textarea value={recordingForm.integrityNotes} onChange={e => setRecordingForm({ ...recordingForm, integrityNotes: e.target.value })} rows={2} placeholder="Recording conditions, equipment used, any interruptions..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Chain of Custody</label>
            <textarea value={recordingForm.chainOfCustody} onChange={e => setRecordingForm({ ...recordingForm, chainOfCustody: e.target.value })} rows={2} placeholder="Who has handled the recording, storage location, access log..." className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => setShowRecordingModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
            <button onClick={addRecording} disabled={!recordingForm.date} className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Add Recording
            </button>
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default CapacityAssessmentTab;
